import { c as _c } from "react/compiler-runtime";
import { CustomLink } from '@/app/_components/CustomLink';
import type { BenefitDetail, BenefitKey, MedianIncomeYear } from '../constants';
import { BENEFIT_ORDER, MEDIAN_INCOME_BY_YEAR } from '../constants';
interface BenefitCardsProps {
  year: MedianIncomeYear;
  /** 사용자 중위소득 비율(%). 0 또는 음수면 안내 카드 표시. */
  percent: number;
}

/** 안내 카드의 4대 급여 요약 한 줄 — 간결 버전. */
const BENEFIT_BRIEF: Record<BenefitKey, string> = {
  edu: '초·중·고 재학생 학비·교과서·교육활동지원비 지원',
  hou: '임차료(세입자) 또는 주택 수선비(자가) 지원',
  med: '진찰·검사·수술·입원 등 의료비 본인부담 최소화',
  liv: '최저 생활 보장을 위한 현금 직접 지급'
};

/**
 * ⑦ 기초생활 4대 급여 자격
 * - 사용자 비율 ≤ b.thresholdPercent 인 급여만 카드로 노출.
 * - 비율 미입력 / 자격 없음일 땐 제도 안내 카드로 대체.
 */
export const BenefitCards = t0 => {
  const $ = _c(5);
  const {
    year,
    percent
  } = t0;
  const {
    benefitDetail
  } = MEDIAN_INCOME_BY_YEAR[year];
  let t1;
  if ($[0] !== benefitDetail || $[1] !== percent) {
    const eligibleBenefits = percent > 0 ? BENEFIT_ORDER.map(k => benefitDetail[k]).filter(b => percent <= b.thresholdPercent) : [];
    const showIntroduction = eligibleBenefits.length === 0;
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:px-16 mobile-tablet:typo-h6">기초생활 4대 급여 자격 확인</h2>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p className="text-Gray-700 typo-body4 mobile-tablet:px-16">기초생활보장 4대 급여는 가구 소득인정액이 중위소득 대비{" "}<strong className="font-700 text-Gray-900">생계 32% · 의료 40% · 주거 48% · 교육 50%</strong>{" "}이하일 때 자격이 됩니다.</p>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t1 = <section className="flex flex-col gap-12">{t2}{t3}{showIntroduction ? <BenefitIntroduction benefitDetail={benefitDetail} /> : <div className="flex flex-col gap-12">{eligibleBenefits.map(_temp2)}</div>}</section>;
    $[0] = benefitDetail;
    $[1] = percent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
interface BenefitIntroductionProps {
  benefitDetail: Record<BenefitKey, BenefitDetail>;
}

/** 자격 없음/미입력 상태에서 보여줄 기초생활보장제도 안내 카드. */
const BenefitIntroduction = t0 => {
  const $ = _c(11);
  const {
    benefitDetail
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="font-700 text-Gray-900 typo-body3">기초생활보장제도란?</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-4">{t1}<p className="text-Gray-700 typo-body4">생활이 어려운 가구에{" "}<strong className="font-700 text-Gray-900">생계비·주거비·의료비·교육비</strong>를 지원하는 공공부조 제도입니다.</p></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <li>① 가구 소득인정액이 급여별 선정 기준 이하일 것</li>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ol className="flex flex-col gap-8 text-Gray-700 typo-body4">{t3}<li>② 의료급여는 부양의무자 기준도 충족해야 함<div className="mt-2 text-Gray-500 typo-caption1">생계·주거·교육급여는 부양의무자 기준 폐지</div></li></ol>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== benefitDetail) {
    t5 = BENEFIT_ORDER.map(key => {
      const benefit = benefitDetail[key];
      const inner = <><div className="text-20 flex size-32 shrink-0 items-center justify-center rounded-small bg-Gray-white"><span aria-hidden="true">{benefit.icon}</span></div><div className="flex flex-col gap-2"><div className="font-700 text-Gray-900 typo-body4">{benefit.name}<span className="ml-8 font-600 text-B-600">{benefit.thresholdPercent}% 이하</span></div><p className="text-Gray-600 typo-caption1">{BENEFIT_BRIEF[key]}</p></div></>;
      return benefit.url ? <CustomLink key={key} href={benefit.url} className="flex items-start gap-12 rounded-small bg-Gray-white p-12 transition-colors hover:bg-Gray-25">{inner}<span aria-hidden="true" className="ml-auto shrink-0 self-center text-Gray-400 typo-body3">→</span></CustomLink> : <div key={key} className="flex items-start gap-12 rounded-small bg-Gray-white p-12">{inner}</div>;
    });
    $[4] = benefitDetail;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div className="grid grid-cols-2 gap-12 mobile-tablet:grid-cols-1">{t5}</div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="border-t-1 border-Gray-200 pt-12 text-center text-Gray-500 typo-caption1">가구원수와 월 소득을 입력하면 받을 수 있는 급여를 바로 확인할 수 있습니다.</p>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = <div className="flex flex-col gap-16 rounded-small bg-Gray-50 p-20 mobile-tablet:mx-16">{t2}{t4}{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
};
function _temp(line, idx) {
  return <li key={idx}>{line}</li>;
}
function _temp2(benefit) {
  const body = <div className="flex min-w-0 flex-1 flex-col gap-8"><div className="flex items-center gap-8"><div className="text-20 flex size-32 items-center justify-center rounded-pill bg-Gray-white"><span aria-hidden="true">{benefit.icon}</span></div><div className="font-700 text-Gray-900 typo-body3">{benefit.name}{" "}<span className="rounded-pill bg-B-50 px-8 py-2 font-600 text-B-600 typo-caption2">중위 {benefit.thresholdPercent}%</span></div></div><p className="text-Gray-700 typo-body4">{benefit.desc}</p><ul className="flex list-disc flex-col gap-4 pl-20 text-Gray-700 typo-caption1">{benefit.list.map(_temp)}</ul></div>;
  return benefit.url ? <CustomLink key={benefit.name} href={benefit.url} className="flex items-start gap-12 rounded-small border-1 border-Gray-100 bg-Gray-25 p-16 mobile-tablet:rounded-none mobile-tablet:border-0 mobile-tablet:border-b-1 mobile-tablet:bg-transparent mobile-tablet:px-16 mobile-tablet:py-12 transition-colors hover:border-B-300 hover:bg-B-25">{body}<span aria-hidden="true" className="shrink-0 self-center text-Gray-400 typo-body3">→</span></CustomLink> : <article key={benefit.name} className="flex items-start gap-12 rounded-small border-1 border-Gray-100 bg-Gray-25 p-16 mobile-tablet:rounded-none mobile-tablet:border-0 mobile-tablet:border-b-1 mobile-tablet:bg-transparent mobile-tablet:px-16 mobile-tablet:py-12">{body}</article>;
}